import * as React from "react";
import { Layout } from "../../layout";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Domains } from "./domains";
import { Quotas } from "./quotas";
import { Workloads } from "./workloads";
import { Agents } from "./agents";
import { HealthOverview } from "./overview";
import { fetchHealth, processHealthOrgChange } from "../../store/healthSlice";
import { HealthHeader } from "./header";
import { IS_DEPLOYMENT_ENV_PROD } from "../../envVariables";
import { ViewModal } from "../../components/modals/viewModal";
import { store } from "../../store";
import { Tabs } from "antd";

export const Health: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const orgName = useAppSelector(selectOrg);
  const isFetched = useAppSelector((state) => state.health.isFetched);

  const basePath = useBasePath("/*");

  React.useEffect(() => {
    if (!isFetched) {
      dispatch(fetchHealth({}));
    }
  }, [isFetched]);

  // Polling interval for health data refresh (30 seconds)
  const HEALTH_POLL_INTERVAL_MS = 30000;

  React.useEffect(() => {
    if (!isFetched) return;

    const intervalId = setInterval(() => {
      // Use silent mode for polling - don't show loading scaffolds
      dispatch(fetchHealth({ silent: true }));
    }, HEALTH_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isFetched, dispatch]);

  React.useEffect(() => {
    dispatch(processHealthOrgChange());
  }, [orgName]);

  const [isDebugging, setIsDebugging] = React.useState(false);

  // Determine active tab from current path
  const activeTab = React.useMemo(() => {
    const path = location.pathname;
    if (path.includes("/health/workloads")) return "workloads";
    if (path.includes("/health/agents")) return "agents";
    if (path.includes("/health/quotas")) return "quotas";
    if (path.includes("/health/domains")) return "domains";
    return "overview";
  }, [location.pathname]);

  const handleTabChange = (key: string) => {
    navigate(`${basePath}/health/${key}`);
  };

  const tabItems = React.useMemo(() => {
    const items = [
      { key: "overview", label: "Overview" },
      { key: "workloads", label: "Workloads" },
      { key: "agents", label: "Agents" },
      { key: "quotas", label: "Quotas" },
      { key: "domains", label: "Domains" },
    ];
    if (!IS_DEPLOYMENT_ENV_PROD) {
      items.push({ key: "debug", label: "Debug" });
    }
    return items;
  }, []);

  const onTabClick = (key: string) => {
    if (key === "debug") {
      setIsDebugging(true);
      return;
    }
    handleTabChange(key);
  };

  return (
    <>
      <Layout key={orgName}>
        <BasePathContext.Provider value={basePath}>
          <HealthHeader />
          <div className="px-6">
            {/* <Tabs
              activeKey={activeTab}
              onChange={onTabClick}
              items={tabItems}
              style={{ marginBottom: 16 }}
            /> */}
            <Routes>
              <Route path={`overview`} element={<HealthOverview />} />
              <Route path={`workloads`} element={<Workloads />} />
              <Route path={`quotas`} element={<Quotas />} />
              <Route path={`domains`} element={<Domains />} />
              <Route path={`agents`} element={<Agents />} />
              <Route path={`*`} element={<Navigate to={`${basePath}/health/overview`} replace />} />
            </Routes>
          </div>
        </BasePathContext.Provider>
      </Layout>
      {isDebugging ? (
        <ViewModal
          kind={"kind"}
          onClose={() => {
            setIsDebugging(false);
          }}
          title={"Domain"}
          object={() => store.getState().health}
          visible={isDebugging}
          filename={`domain-${name}`}
        />
      ) : null}
    </>
  );
};
